import { Box, styled, Typography } from '@mui/material'
import { Icon, LabelChipVariant, IconDictionary, Avatar, AvatarSize, theme, Chip } from '@ketch-com/deck'
import { useState, useMemo, useRef } from 'react'
import { MorePlatformsModal } from 'pages/dataSentry/data/dataTabComponents/MorePlatformsModal'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import { Tooltip } from '@mui/material'
import { SystemsByClassificationDetail } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { useIsOverflow } from 'pages/dataSentry/trackers/hooks/useIsOverflow'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'

const CardContainer = styled(Box)({
  width: '100%',
  maxWidth: '405px',
  minWidth: '0',
  minHeight: '120px',
  padding: '16px',
  backgroundColor: theme.palette.Common.Container,
  borderRadius: '12px',
  display: 'flex',
  marginBottom: '6px',
  border: '1px solid #f0f0f0',
})

const LeftSection = styled(Box)({
  width: '125px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
})

const DividerContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  margin: '0 16px',
})

const Divider = styled(Box)({
  width: '1px',
  height: '100%',
  backgroundColor: theme.palette.Common.Divider,
})

const SyncIconWrapper = styled(Box)({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.Common.Container,
})

const RightSection = styled(Box)<{ platformCount: number }>(({ platformCount }) => ({
  width: '210px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}))

const PlatformList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  paddingRight: '16px',
})

const PlatformItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  marginBottom: '4px',
})

const PlatformIcon = styled(Box)({
  position: 'relative',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const PlatformName = styled(Typography)({
  flex: 1,
  whiteSpace: 'normal',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.2,
  fontSize: '14px',
  paddingRight: '16px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  width: '100%',
})

export interface DataCategoryCardProps {
  classification: SystemsByClassificationDetail
}

export const DataCategoriesSharingItem = ({ classification }: DataCategoryCardProps) => {
  const { setDataClassificationDetails } = useDataSentryContext()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { name: categoryName, code: categoryCode } = classification?.classification || {}
  const dataSystems = useMemo(() => classification?.dataSystems || [], [classification])

  const visiblePlatforms = useMemo(() => dataSystems.slice(0, 4), [dataSystems])
  const remainingCount = useMemo(() => Math.max(0, dataSystems.length - 4), [dataSystems])
  const titleRef = useRef<HTMLDivElement>(null)
  const isTitleOverflow = useIsOverflow({ ref: titleRef, overflowType: 'width' })

  return (
    <CardContainer>
      <LeftSection>
        <Tooltip title={isTitleOverflow ? categoryName : ''} placement="top">
          <Box
            key={categoryName}
            onClick={() => {
              setDataClassificationDetails?.({
                type: ClassificationType.DATA_CATEGORY,
                name: categoryName || '',
                code: categoryCode || '',
              })
            }}
          >
            <AssetsTabTableLabelChip
              key={categoryName}
              customTitleComponent={
                <Typography
                  ref={titleRef}
                  component="div"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: '100px',
                  }}
                >
                  {categoryName}
                </Typography>
              }
              variant={LabelChipVariant.category}
              hideConfidence
              hideIcon
              isViewOnly
            />
          </Box>
        </Tooltip>
      </LeftSection>
      <DividerContainer>
        <Divider />
        <SyncIconWrapper>
          <Icon name={IconDictionary.ORefresh} width={24} height={24} iconColor={theme.palette.Black.o32} />
        </SyncIconWrapper>
      </DividerContainer>
      <RightSection platformCount={visiblePlatforms.length}>
        <PlatformList>
          {visiblePlatforms.map(platform => (
            <PlatformItem key={platform.dataSystem?.code}>
              <PlatformIcon>
                {platform.dataSystem?.logoUrl ? (
                  <Avatar
                    src={platform.dataSystem.logoUrl}
                    variant="circular"
                    borderColor={theme.palette.Black.o8}
                    size={AvatarSize.medium}
                    sx={{ padding: '6px' }}
                    isLogo
                  />
                ) : (
                  <Avatar
                    sx={{ padding: '6px' }}
                    icon="NoLogo"
                    textColor={theme.palette.Text.Secondary}
                    borderColor={theme.palette.Black.o8}
                    size={AvatarSize.medium}
                    isLogo
                  />
                )}
              </PlatformIcon>
              <PlatformName variant="smallBody">{platform.dataSystem?.name || platform.dataSystem?.code}</PlatformName>
            </PlatformItem>
          ))}
        </PlatformList>
        {remainingCount > 0 && (
          <Chip
            sx={{ padding: '2px 12px', width: '65px', marginTop: '16px' }}
            label={`${remainingCount} More`}
            clickable
            onClick={() => setIsModalOpen(true)}
            size="small"
          />
        )}
      </RightSection>
      {isModalOpen && (
        <MorePlatformsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          categoryName={categoryName || 'Unknown Category'}
          platforms={dataSystems}
        />
      )}
    </CardContainer>
  )
}
